import React, { FC } from 'react';

import { AllieCard, AllieEllipse, AllieText } from '../../../components';
import {
  ProductionOrderStatus,
  AllieProductionOrderStatusChip,
} from '../ProductionOrderStatusChip/ProductionOrderStatusChip';
import { StationSummaryStyled } from '../StationSummaryCard/StationSummaryCard.styles';
import {
  FlexContainer,
  StyledStationTitleLink,
  StyledStationButton,
} from '../StationSummaryCardV2/StationListSummaryCard.styles';
import { OEEContainer } from '../StationSummaryCardV2/components/OEEContainer';
import { POField } from '../StationSummaryCardV2/components/POField';
import { ProgressContainer } from '../StationSummaryCardV2/components/ProgressContainer';
import { ShiftField } from '../StationSummaryCardV2/components/ShiftField';
import { Stats } from '../StationSummaryCardV2/components/Stats';
import { StationSummaryCardProps } from '../StationSummaryCardV2/StationSummaryCard.types';
// eslint-disable-next-line
import { SuspendedLine } from '../StationListSummaryCardV2/components/SuspendedLine/SuspendedLine';
import { AllieLineStatusChip } from '../LineStatusChip';

const mapProductionStatusBorderColor: Record<string, string> = {
  in_progress: 'none',
  stopped: 'var(--allie-color-error-main)',
  paused: 'none',
  no_productions: 'none',
  technical_stop: 'none',
  planned_stop: 'var(--allie-color-neutral-70)',
  unplanned_stop: 'var(--allie-color-error-main)',
};

export interface StationSummaryCardV3Props extends StationSummaryCardProps {
  stationLabelStatus: string;
  production: { status: string; legacyId: string; stationStatus: string };
  labels: StationSummaryCardProps['labels'] & {
    suspendedTitle?: string;
    suspendedSubtitle?: string;
    suspendedLastUpdate?: string;
  };
}

export const AllieStationSummaryCardV3: FC<StationSummaryCardV3Props> = ({
  title,
  data,
  barData,
  labels,
  onClick,
  className,
  isProductive,
  lastSectionType,
  production,
  OEE,
  statusLabel,
  shiftEndsAt,
  shiftName,
  shiftStartsAt,
  href,
  trackingOnClickEvent,
  isTablet = false,
  isMobile = false,
  stationLabelStatus,
}) => {
  const isTechnicalStop =
    lastSectionType === 'neutral' && production.status === ProductionOrderStatus.STOPPED;

  if (production.stationStatus === 'SUSPENDED') {
    return (
      <StationSummaryStyled className={className}>
        <AllieCard
          onClick={() => isMobile && onClick()}
          style={{
            borderRadius: '8px',
            padding: '12px 16px',
            boxSizing: 'border-box',
            height: '371px',
            // eslint-disable-next-line max-len
            border: `2px solid ${mapProductionStatusBorderColor[production.status]}`,
          }}
          content={{
            style: {
              padding: 0,
              height: '100%',
            },
            children: (
              <FlexContainer style={{ height: '100%' }} gap={8} flexDirection="column">
                <div style={{ width: 'fit-content' }}>
                  <AllieLineStatusChip
                    text={stationLabelStatus}
                    lineStatus={production.stationStatus}
                  />
                </div>
                <div>
                  <StyledStationTitleLink
                    isMobile={isMobile}
                    onClick={() =>
                      trackingOnClickEvent && trackingOnClickEvent('Station Name', title)
                    }
                    href={href}
                  >
                    <span>{title}</span>
                  </StyledStationTitleLink>
                </div>

                <FlexContainer
                  gap={13}
                  alignItems={isMobile ? 'flex-start' : 'center'}
                  justifyContent={'space-between'}
                  style={{ height: '100%' }}
                >
                  <SuspendedLine
                    title={labels.suspendedTitle}
                    subtitle={labels.suspendedSubtitle}
                    lastUpdate={labels.suspendedLastUpdate}
                    isCard
                  />
                </FlexContainer>
              </FlexContainer>
            ),
          }}
        />
      </StationSummaryStyled>
    );
  }

  return (
    <StationSummaryStyled className={className}>
      <AllieCard
        onClick={() => isMobile && onClick()}
        style={{
          borderRadius: '8px',
          padding: '12px 16px',
          boxSizing: 'border-box',
          // eslint-disable-next-line max-len
          border: `2px solid ${mapProductionStatusBorderColor[production.status]}`,
        }}
        content={{
          style: {
            padding: 0,
          },
          children: (
            <FlexContainer gap={8} flexDirection="column">
              <div style={{ width: 'fit-content' }}>
                <AllieLineStatusChip
                  text={stationLabelStatus}
                  lineStatus={production.stationStatus}
                />
              </div>
              <FlexContainer flexDirection="column" gap={11}>
                <FlexContainer
                  gap={13}
                  alignItems={isMobile ? 'flex-start' : 'center'}
                  justifyContent={'space-between'}
                >
                  <StyledStationTitleLink
                    isMobile={isMobile}
                    onClick={() =>
                      trackingOnClickEvent && trackingOnClickEvent('Station Name', title)
                    }
                    href={href}
                  >
                    <span>{title}</span>
                  </StyledStationTitleLink>
                  <AllieProductionOrderStatusChip
                    status={
                      isTechnicalStop ? ProductionOrderStatus.TECHNICAL_STOP : production.status
                    }
                    statusLabel={statusLabel}
                  />
                </FlexContainer>
                <FlexContainer
                  gap={10}
                  justifyContent="space-between"
                  alignItems="center"
                  style={{
                    padding: '6px',
                    background: 'var(--allie-color-neutral-10)',
                    borderRadius: 'var(--allie-radius-xs)',
                  }}
                >
                  <POField label={labels.productionOrder} id={production.legacyId} />
                  <ShiftField shiftName={shiftName} />
                </FlexContainer>
                <FlexContainer gap={32} style={{ padding: '0 4px' }}>
                  <Stats data={data} layout="card" />
                  <OEEContainer value={OEE} isTablet={false} />
                </FlexContainer>

                <ProgressContainer
                  barData={barData}
                  shiftEndsAt={shiftEndsAt}
                  shiftStartsAt={shiftStartsAt}
                  progressBarHeight="27px"
                />
                <StyledStationButton
                  type="button"
                  allieType="secondary"
                  allieColor="neutral"
                  fill="outline"
                  allieSize={isMobile ? 'small' : 'xs'}
                  isMobile={isMobile}
                  full
                  className="see-detail"
                  onClick={() => {
                    onClick();
                    trackingOnClickEvent && trackingOnClickEvent('View more detail button', title);
                  }}
                >
                  <AllieText type={isMobile ? 'button-normal' : 'body3'}>{labels.button}</AllieText>
                </StyledStationButton>
              </FlexContainer>
            </FlexContainer>
          ),
        }}
      />
    </StationSummaryStyled>
  );
};
